import Decimal from "decimal.js";

export interface CryptoFeeEstimate {
	amountCents: number;
	percentageFeeCents: number;
	gasFeeCents: number;
	creditedCents: number;
}

const DEFAULT_CRYPTO_DECIMALS = 3;
const MAX_CRYPTO_DECIMALS = 18;

export const formatCryptoMinorUnits = (amount: number, decimals: number): string => {
	const safeDecimals = Number.isInteger(decimals) && decimals >= 0
		? Math.min(decimals, MAX_CRYPTO_DECIMALS)
		: DEFAULT_CRYPTO_DECIMALS;
	return new Decimal(amount || 0)
		.div(new Decimal(10).pow(safeDecimals))
		.toFixed(safeDecimals)
		.replace(/\.?0+$/, "");
};

export const parseUsdCents = (value: string): number | null => {
	const normalized = value.trim();
	if (!normalized) return null;

	try {
		const amount = new Decimal(normalized);
		if (!amount.isFinite() || amount.isNegative()) return null;
		const cents = amount.mul(100).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
		return Number.isSafeInteger(cents) ? cents : null;
	} catch {
		return null;
	}
};

export const calculateCryptoFeeEstimate = (
	amountCents: number,
	feePercent: number,
	gasFeeCents: number,
): CryptoFeeEstimate => {
	const percentageFeeCents = new Decimal(amountCents)
		.mul(feePercent)
		.div(10_000)
		.toDecimalPlaces(0, Decimal.ROUND_HALF_UP)
		.toNumber();

	return {
		amountCents,
		percentageFeeCents,
		gasFeeCents,
		creditedCents: amountCents - percentageFeeCents - gasFeeCents,
	};
};

export const getCheapestUsableCryptoCoinKey = (coins: ICryptoCoin[]): string | null => {
	const usable = coins.filter(coin => coin.usable);
	if (!usable.length) return null;
	return usable.reduce((best, coin) => coin.gas_fee_cents < best.gas_fee_cents ? coin : best).coin_key;
};
